import {
  ActiveFileType,
  usePaymentDetail,
} from '@/components/InvoiceReportDetailDrawer/context/PaymentDetailContext';
import { FileInfoDrawerContainer } from '@/components/InvoiceReportDetailDrawer/FileInfoDrawer';
import { Contents } from '@/components/InvoiceReportDetailDrawer/FileInfoDrawer/Contents';
import { useFileInfoDrawerContext } from '@/components/InvoiceReportDetailDrawer/FileInfoDrawer/context/FileInfoDrawerContext';
import { useInvoiceReportBusinessDocumentService } from '@/context/services/invoice_reports/InvoiceReportBusinessDocument.service';
import { useInvoiceReportsSearchRefetch } from '@/components/InvoiceReportDetailDrawer/context/InvoiceReportsSearchRefetchContext';
import { Loading } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, Suspense, useCallback, useState } from 'react';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);

type Props = {
  onCloseDetailDrawer?: () => void;
};

type FetcherProps = Props & {
  businessDocumentId: string;
  onFileNameLoaded: (fileName: string) => void;
};

const FallbackLoading: FC = () => (
  <div className={cx('loading-container')}>
    <Loading size='large' color='default' />
  </div>
);

const InnerContent: FC<FetcherProps> = memo(
  ({ businessDocumentId, onFileNameLoaded, onCloseDetailDrawer }) => {
    const { data: businessDoc, error } =
      useInvoiceReportBusinessDocumentService(businessDocumentId);
    const fnCheckPermission = useInvoiceReportsSearchRefetch();

    return (
      <Contents
        businessDoc={businessDoc}
        error={error}
        onFileNameLoaded={onFileNameLoaded}
        onCloseDetailDrawer={onCloseDetailDrawer}
        fnCheckPermission={fnCheckPermission}
      />
    );
  }
);
InnerContent.displayName = 'InnerContent';

const BusinessDocumentDrawerFetcher: FC<FetcherProps> = memo((props) => {
  return (
    <Suspense fallback={<FallbackLoading />}>
      <InnerContent {...props} />
    </Suspense>
  );
});
BusinessDocumentDrawerFetcher.displayName = 'BusinessDocumentDrawerFetcher';

export const BusinessDocumentDrawerContainer: FC<Props> = memo(
  ({ onCloseDetailDrawer }) => {
    const { open } = useFileInfoDrawerContext();
    const { activeFile } = usePaymentDetail();
    const [fileName, setFileName] = useState('');

    const handleFileNameLoaded = useCallback((name: string) => {
      setFileName(name);
    }, []);

    const businessDocumentId =
      activeFile?.type === ActiveFileType.BUSINESS_DOCUMENT
        ? activeFile.id
        : undefined;

    return (
      <FileInfoDrawerContainer title={fileName}>
        {open && businessDocumentId ? (
          <BusinessDocumentDrawerFetcher
            businessDocumentId={businessDocumentId}
            onFileNameLoaded={handleFileNameLoaded}
            onCloseDetailDrawer={onCloseDetailDrawer}
          />
        ) : null}
      </FileInfoDrawerContainer>
    );
  }
);
BusinessDocumentDrawerContainer.displayName = 'BusinessDocumentDrawerContainer';
